import { Formik, Form } from 'formik'
import { useState, useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { useOpenInWindow } from 'use-open-window'
import * as Yup from 'yup'
import { Button } from '@/components/button'
import { SocialButton } from '@/components/button'
import { Row, Group } from '@/components/form'
import Icon from '@/components/icon'
import { toast } from '@/components/toast-notifications'
import ENV_VARS from '@/config'
import { verifyUsername } from '@/models/auth/ports'
import { breakpoints } from '@/styles'
import handleSession from '@/utils/handle-session'
import OrDiv from './or-div'
import TermsAndPrivacyPolicyDiv from './terms-and-privacy-div'
import HeaderDiv from './header-div'
import EmailFieldInput from './email-fieldinput'
import Context from '../context'

const { BACKEND_SERVER } = ENV_VARS

const ButtonsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;
  width: 100%;

  @media (max-width: ${breakpoints.phoneMax}) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`

const StyledForm = styled(Form)`
  width: 100%;
`

const LoginButtonWrapper = styled(Group)`
  text-align: center;
  margin: 0 auto;
`

const LoginSocial = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [selectedPlatform, setSelectedPlatform] = useState('')
  const { username, setSocial, setExistingSocialState, setCurrentStep } = useContext(Context)

  const socialLoginGroup = {
    google: {
      icon: 'google_icon',
      url: `${BACKEND_SERVER}/social_login/google/sign_in`
    },
    facebook: {
      icon: 'facebook_icon',
      url: `${BACKEND_SERVER}/social_login/facebook/sign_in`
    },
    apple: {
      icon: 'apple_icon',
      url: `${BACKEND_SERVER}/social_login/apple/sign_in`
    }
  }

  const validationSchema = Yup.object().shape({
    username: Yup.string().phoneEmail()
  })

  const options = {
    centered: true,
    specs: {
      width: 800,
      height: 600
    }
  }

  const [handleWindowOpen, newWindowHandle] = useOpenInWindow(options)

  const loginSocial = async (values, { setSubmitting }) => {
    const { body } = await verifyUsername(values)
    setSubmitting(false)

    if (
      body.login_with_email_enabled === false &&
      body.login_with_social_account_enabled === true
    ) {
      toast(`use your ${body.connected_social_providers[0]} provider to login`, {
        title: t('b.auth.view.login_social.failed.title', 'Login Failed'),
        type: 'error'
      })
      return
    }

    setCurrentStep('password')
  }

  useEffect(() => {
    const handleSocialLoginEvent = async event => {
      if (event.data.source_message === 'mb_social_login') {
        setSocial({ platform: selectedPlatform, data: event.data })
        if (!newWindowHandle) return
        newWindowHandle.close()
        switch (event.data.message_key) {
          case 'msg.login_with_social_login_unauthorized':
          case 'msg.login_with_social_login_unauthorized_by_provider':
            setExistingSocialState(true)
            setCurrentStep('existingSocialUser')
            break
          case 'err.user_does_not_exist':
            toast(
              t(
                'b.auth.view.login_social.failed.description',
                'No account found. Please check your account details or sign up.'
              ),
              {
                title: t('b.auth.view.login_social.failed.title', 'Login Failed'),
                type: 'error'
              }
            )
            break
          case 'err.unauthorized_token':
          case 'err.email_was_not_sent_by_provider':
            toast(event?.data?.message, {
              title: t('b.auth.view.login_social.failed.title', 'Login Failed'),
              type: 'error'
            })
            return
          default:
            if (event.data.new === 0) {
              await handleSession(dispatch.session.loadSession())
              return
            }
        }
      }
    }
    window.addEventListener('message', handleSocialLoginEvent)
    return () => window.removeEventListener('message', handleSocialLoginEvent)
  }, [
    setExistingSocialState,
    newWindowHandle,
    setCurrentStep,
    setSocial,
    selectedPlatform,
    dispatch.session,
    t
  ])

  return (
    <>
      <HeaderDiv
        headingText={t('b.landing.view.welcome_back_login.text', 'Welcome back! Log in below.')}
        hasSignUpHereLink
      />
      <Formik
        initialValues={{ username: username || '' }}
        onSubmit={loginSocial}
        validationSchema={validationSchema}
      >
        {({ isSubmitting }) => (
          <StyledForm noValidate>
            <Row>
              <Group col={12}>
                <EmailFieldInput autoFocus />
              </Group>
            </Row>
            <Row>
              <LoginButtonWrapper col={12}>
                <Button
                  id="web-LoginSocial-continue-button"
                  theme="primary"
                  type="submit"
                  width="100%"
                  disabled={isSubmitting}
                >
                  {t('l.incomplete_modal.continue.text', 'Continue')}
                </Button>
              </LoginButtonWrapper>
            </Row>
            <OrDiv justOr={false} orText={t('b.auth.view.or_login_with.text', 'OR LOG IN WITH')} />
            <ButtonsWrapper>
              {Object.keys(socialLoginGroup).map((item, index) => {
                const socialItem = socialLoginGroup[item]
                return (
                  <SocialButton
                    id={`web-LoginSocial-${item}-submit-button`}
                    key={index}
                    onClick={event => {
                      handleWindowOpen(event, {
                        url: socialItem.url
                      })
                      setSelectedPlatform(item)
                    }}
                  >
                    <Icon id={socialItem.icon} width={20} height={20} />
                    {item.replace(/\b[a-zA-Z]/g, match => match.toUpperCase())}
                  </SocialButton>
                )
              })}
            </ButtonsWrapper>
            <TermsAndPrivacyPolicyDiv />
          </StyledForm>
        )}
      </Formik>
    </>
  )
}

export default LoginSocial
